'use client'

import { forwardRef } from "react"
import { Switch as SwitchMain } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"

/**
 * Renders a switch component.
 *
 * @param {Object} props - The component props.
 * @param {boolean} props.checked - The current checked state of the switch.
 * @param {function} props.onChange - The callback function to handle switch state changes.
 * @returns {JSX.Element} The rendered switch component.
 */

interface SwitchProps {
  checked: boolean
  onChange: (_checked: boolean) => void
  [key: string]: any
}

export function Switch({ checked, onChange, ...props }: SwitchProps) {
  return (<SwitchMain checked={checked} onCheckedChange={onChange} {...props} />)
}

/**
 * Renders a switch label component.
 *
 * @param {Object} props - The component props.
 * @param {boolean} props.checked - The checked state of the switch.
 * @param {function} props.onChange - The function to handle the switch change event.
 * @param {string} props.title - The title of the switch label.
 * @returns {JSX.Element} The rendered switch label component.
 */

interface SwitchLabelProps {
  checked: boolean
  onChange: (_checked: boolean) => void
  title: string
  [key: string]: any
}

export function SwitchLabel({ checked, onChange, title, ...props }: SwitchLabelProps) {
  return (
    <div className="flex items-center space-x-3">
      <SwitchMain checked={checked} onCheckedChange={onChange} {...props} />
      <Label>{title}</Label>
    </div>
  )
}


/**
 * Renders a switch card component.
 *
 * @param {Object} props - The component props.
 * @param {boolean} props.checked - The checked state of the switch.
 * @param {function} props.onChange - The callback function to handle switch state changes.
 * @param {string} props.title - The title of the switch card.
 * @param {string} props.description - The description of the switch card.
 * @param {string} props.className - Additional CSS class names for the switch card.
 * @param {Object} props... - Additional props to be spread onto the root element.
 * @returns {JSX.Element} The rendered switch card component.
 */

interface SwitchCardProps {
  checked: boolean
  onChange: (_checked: boolean) => void
  title: string
  description: string
  className?: string
  color?: string
  [key: string]: any
}

// @ts-ignore
export const SwitchCard = forwardRef<HTMLDivElement, SwitchCardProps>(({ checked, onChange, title, description, className, color, ...props }, ref) => (
  <div ref={ref} className={`flex justify-between items-center gap-4 bg-background border border-input p-4 rounded-lg ${className || ''}`}>
    <div className="flex flex-col gap-1">
      <h4 className="text-foreground text-sm leading-5 tracking-[.0125em]">{title}</h4>
      <p className="text-xs leading-5 text-muted-foreground">{description}</p>
    </div>
    <SwitchMain checked={checked} onCheckedChange={onChange} color={color} {...props} />
  </div>
))

SwitchCard.displayName = "SwitchCard"